import styles from './FilterPopover.module.scss';
import React, { ReactNode } from 'react';
import { FaroButton, FaroPopover, FaroLabel, FaroOverlayProps } from '@faro/design-system';
import { Form, Formik } from 'formik';
import { FaroToggleField } from '@faro/forms';
import { Filters } from './FilterPopover.types';
import { isEqual, pickBy } from 'lodash';

export interface FilterPopoverProps extends Omit<FaroOverlayProps, 'children'> {
    setFilters: (f: Filters) => void;
    filters: Filters;
    initialFilterValues: Filters;
    onClose: () => void;
}

const defaultValues = {
    includeDeleted: false,
};

const FilterPopover = (props: FilterPopoverProps): ReactNode => {
    const { className = '', setFilters, filters, initialFilterValues, onClose, ...rest } = props;

    const submitFilters = async (values: Filters) => {
        const newFilters: Filters = pickBy(values, (v, k) => !isEqual((initialFilterValues as any)[k], v));
        setFilters(newFilters);
    };

    // Undefined state causes isEqual diff logic to be ineffective at determining if filters were explicitly set
    const initialValues = {
        includeDeleted: filters.includeDeleted ?? false,
    };

    return (
        <FaroPopover className={`${className} `} {...rest}>
            <Formik
                className={className}
                initialValues={initialValues}
                onSubmit={submitFilters}
                enableReinitialize={true}
            >
                {formikProps => {
                    const { submitForm, values, setValues, dirty } = formikProps;
                    const showReset = !isEqual(defaultValues, values);
                    const showSaveCancel = dirty;
                    const showAnyControl = showSaveCancel || showReset;
                    return (
                        <Form>
                            <FaroPopover.Body className={styles.popoverBody}>
                                <div className={styles.toggleFieldGroup}>
                                    <FaroToggleField
                                        id="includeDeleted"
                                        name="includeDeleted"
                                        value={values.includeDeleted}
                                    />
                                    <FaroLabel htmlFor="includeDeleted">Show deleted definitions</FaroLabel>
                                </div>
                            </FaroPopover.Body>
                            {showAnyControl && (
                                <FaroPopover.Footer className={styles.footer}>
                                    <FaroButton
                                        className={`${styles.resetControl} ${showReset ? styles.show : ''}`}
                                        appearance="inline"
                                        data-automation-id="buttonCancel"
                                        onClick={() => {
                                            setValues(defaultValues);
                                            submitForm();
                                            onClose();
                                        }}
                                    >
                                        Reset Filters
                                    </FaroButton>
                                    <div
                                        className={`${styles.saveCancelControls} ${showSaveCancel ? styles.show : ''}`}
                                    >
                                        <FaroButton
                                            appearance="solid"
                                            data-automation-id="buttonCancel"
                                            onClick={() => {
                                                onClose();
                                            }}
                                        >
                                            Cancel
                                        </FaroButton>
                                        <FaroButton
                                            disabled={false}
                                            loading={false}
                                            color="primary"
                                            onClick={async () => {
                                                submitForm();
                                                onClose();
                                            }}
                                        >
                                            Apply
                                        </FaroButton>
                                    </div>
                                </FaroPopover.Footer>
                            )}
                        </Form>
                    );
                }}
            </Formik>
        </FaroPopover>
    );
};

export default FilterPopover;
